import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Plus } from 'lucide-react';

/** Une fiche proposée par la recherche. */
export interface OptionRecherchable {
  id: string;
  libelle: string;
  /** Seconde ligne : fonction, SIRET, ville… Elle est aussi cherchée. */
  detail?: string | null;
  /** Termes cherchés sans être affichés, un SIRET sans espaces par exemple. */
  motsCles?: string;
  /** Repère à droite de la ligne, une pastille le plus souvent. */
  complement?: ReactNode;
}

/** Recherche sans casse ni accent, comme on la tape. */
function normaliserRecherche(valeur: string): string {
  return valeur
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Champ de recherche parmi des fiches existantes, avec création à défaut.
 *
 * Le choix ne change qu'à la sélection d'une ligne : taper filtre la liste
 * sans défaire ce qui est choisi, et quitter le champ y remet le nom retenu.
 * Le clavier suffit — flèches, Entrée, Échap — et chaque ligne a la hauteur
 * d'une cible tactile.
 */
export function ChampRecherchable({
  id,
  libelle,
  placeholder,
  options,
  valeur,
  onChoisir,
  creation,
  aucunResultat = 'Aucune fiche ne correspond.',
  desactive = false,
  invalide = false,
  classeLibelle = 'text-sm font-medium text-codialis-navy',
}: {
  id: string;
  libelle: string;
  placeholder: string;
  options: OptionRecherchable[];
  valeur: string | null;
  onChoisir: (id: string) => void;
  /** Proposée en fin de liste quand la personne n'est pas encore mémorisée. */
  creation?: { libelle: string; onCreer: (recherche: string) => void };
  aucunResultat?: string;
  desactive?: boolean;
  invalide?: boolean;
  /** Le libellé suit le formulaire qui accueille le champ. */
  classeLibelle?: string;
}): JSX.Element {
  const choisie = options.find((option) => option.id === valeur) ?? null;
  const [recherche, setRecherche] = useState(choisie?.libelle ?? '');
  const [ouvert, setOuvert] = useState(false);
  const [active, setActive] = useState(0);
  const conteneur = useRef<HTMLDivElement>(null);
  const idListe = `${useId()}-liste`;

  // Le nom retenu suit la sélection, y compris quand elle vient du serveur.
  useEffect(() => {
    if (!ouvert) setRecherche(choisie?.libelle ?? '');
  }, [choisie?.libelle, ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const surClicExterieur = (evenement: MouseEvent): void => {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', surClicExterieur);
    return () => document.removeEventListener('mousedown', surClicExterieur);
  }, [ouvert]);

  const filtrees = useMemo(() => {
    const terme = normaliserRecherche(recherche);
    // Le nom déjà retenu affiché dans le champ n'est pas une recherche.
    if (!terme || (choisie && recherche === choisie.libelle)) return options;
    return options.filter((option) =>
      normaliserRecherche(`${option.libelle} ${option.detail ?? ''} ${option.motsCles ?? ''}`).includes(terme),
    );
  }, [recherche, options, choisie]);

  const lignes = filtrees.length + (creation ? 1 : 0);
  const choisirLigne = (index: number): void => {
    const option = filtrees[index];
    setOuvert(false);
    if (option) {
      setRecherche(option.libelle);
      onChoisir(option.id);
    } else if (creation) {
      creation.onCreer(choisie && recherche === choisie.libelle ? '' : recherche.trim());
    }
  };

  const classeLigne = (index: number): string =>
    `flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left text-sm ${
      index === active ? 'bg-codialis-sand' : 'bg-white'
    }`;

  return (
    <div ref={conteneur} className="relative min-w-0">
      <label htmlFor={id} className={`block ${classeLibelle}`}>
        {libelle}
      </label>
      <div className="relative mt-1">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={ouvert}
          aria-controls={idListe}
          aria-autocomplete="list"
          aria-activedescendant={ouvert && lignes > 0 ? `${idListe}-${active}` : undefined}
          aria-invalid={invalide || undefined}
          autoComplete="off"
          disabled={desactive}
          value={recherche}
          placeholder={placeholder}
          onChange={(evenement) => {
            setRecherche(evenement.target.value);
            setActive(0);
            setOuvert(true);
          }}
          // La liste s'ouvre au geste — clic, frappe, flèche — et pas à la
          // prise de focus : une fenêtre qui se ferme rend le focus au champ,
          // et la liste rouverte empêchait d'y afficher le choix qui venait
          // d'être fait (une entreprise tout juste créée, par exemple).
          onFocus={(evenement) => evenement.target.select()}
          onClick={() => setOuvert(true)}
          onKeyDown={(evenement) => {
            if (evenement.key === 'ArrowDown') {
              evenement.preventDefault();
              setOuvert(true);
              setActive((index) => Math.min(lignes - 1, index + 1));
            } else if (evenement.key === 'ArrowUp') {
              evenement.preventDefault();
              setActive((index) => Math.max(0, index - 1));
            } else if (evenement.key === 'Enter' && ouvert && lignes > 0) {
              evenement.preventDefault();
              choisirLigne(active);
            } else if (evenement.key === 'Escape' && ouvert) {
              // Échap referme la liste, pas la fenêtre qui contient le champ.
              evenement.stopPropagation();
              setOuvert(false);
              setRecherche(choisie?.libelle ?? '');
            }
          }}
          className={`min-h-[44px] w-full rounded-xl border bg-white py-2.5 pl-3 pr-10 text-sm text-codialis-navy outline-none transition focus:ring-4 disabled:cursor-not-allowed disabled:bg-gray-50 ${
            invalide
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-gray-200 focus:border-codialis-sky focus:ring-sky-100'
          }`}
        />
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
          aria-hidden
        />
      </div>

      {ouvert && !desactive ? (
        <ul
          id={idListe}
          role="listbox"
          aria-label={libelle}
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {filtrees.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-codialis-gray" role="presentation">
              {aucunResultat}
            </li>
          ) : null}
          {filtrees.map((option, index) => (
            <li
              key={option.id}
              id={`${idListe}-${index}`}
              role="option"
              aria-selected={option.id === valeur}
              // Garder le focus dans le champ : un clic qui le lui retire
              // fermerait la liste avant que le choix soit pris.
              onMouseDown={(evenement) => evenement.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => choisirLigne(index)}
              className={classeLigne(index)}
            >
              <span className="min-w-0">
                <span
                  className={`block truncate ${option.id === valeur ? 'font-bold' : 'font-semibold'} text-codialis-navy`}
                >
                  {option.libelle}
                </span>
                {option.detail ? (
                  <span className="block truncate text-xs text-codialis-gray">{option.detail}</span>
                ) : null}
              </span>
              {option.complement ? <span className="shrink-0">{option.complement}</span> : null}
            </li>
          ))}
          {creation ? (
            <li
              id={`${idListe}-${filtrees.length}`}
              role="option"
              aria-selected={false}
              onMouseDown={(evenement) => evenement.preventDefault()}
              onMouseEnter={() => setActive(filtrees.length)}
              onClick={() => choisirLigne(filtrees.length)}
              className={`${classeLigne(filtrees.length)} border-t border-gray-100 font-semibold text-codialis-blue`}
            >
              <span className="flex items-center gap-2">
                <Plus className="h-4 w-4 shrink-0" aria-hidden />
                {creation.libelle}
              </span>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
